import { Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CursorService } from './cursor.service';
import { MapService } from './map.service';
import { Position } from 'src/app/models/position';
import { FollowModeState } from 'src/app/models/follow-mode-states';
import { DisplayMode } from 'src/app/models/display-mode';
import { LocationService } from '../platform/location.service';
import { UtilsService } from '../utils/utils.service';
import { Subscription } from 'rxjs';
import { WaypointService } from '../core/waypoint.service';
import { MobileResolutionService } from '../platform/mobile-resolution.service';

interface MotionState {
  lon: number;
  lat: number;
  bearing: number;
  pitch: number;
  zoom: number;
}

@Injectable({
  providedIn: 'root'
})
export class NavigationMotionControllerService {

  private readonly MIN_SPEED_FOR_GPS_BEARING = 3; // km/h
  private readonly BEARING_SMOOTHING = 0.15;
  private readonly MIN_ZOOM = 3;
  private readonly MAX_ZOOM = 19;
  private readonly ZOOM_STEP = 1;
  private readonly ZOOM_ANIMATION_DURATION = 250;
  private readonly MIN_DIST_FOR_BEARING_METERS = 0.5; // tweak

  /** Speed-adaptive zoom (follow mode only): slower = zoomed in, faster = pulled back. */
  private readonly AUTO_ZOOM_ENABLED = true;
  /** km/h breakpoints, sorted ascending, linearly interpolated + clamped at ends. */
  private readonly ZOOM_SPEED_CURVE: { speedKmh: number; zoom: number }[] = [
    { speedKmh: 0, zoom: 17.5 },   // stopped / dense city
    { speedKmh: 30, zoom: 16.5 },  // urban
    { speedKmh: 60, zoom: 15.5 },  // main road
    { speedKmh: 100, zoom: 15.0 }, // highway
    { speedKmh: 140, zoom: 15.0 }  // fast
  ];
  /** EMA factor smoothing raw GPS speed to avoid jittery zoom. */
  private readonly SPEED_SMOOTHING = 0.25;
  /** Per-frame factor gliding state.zoom toward the target (smooth, not snappy). */
  private readonly AUTO_ZOOM_EASE = 0.08;

  /** How long the freshly calculated range stays fully framed before following resumes. */
  private readonly RANGE_OVERVIEW_HOLD_MS = 5000;
  /** Camera flight used when following takes over from that overview. */
  private readonly RANGE_OVERVIEW_RESUME_MS = 1200;

  /** Gap kept between the followed cursor and the top of the bottom nav-plan panel. */
  private readonly CURSOR_PANEL_CLEARANCE = 48;
  /** Cursor's allowed vertical band, as fractions of the viewport height from the top. */
  private readonly CURSOR_MIN_Y_FRACTION = 0.35;
  private readonly CURSOR_MAX_Y_FRACTION = 0.8;

  private navPlanCard: Element | null = null;

  private state: MotionState = {
    lon: 0,
    lat: 0,
    bearing: 0,
    pitch: 0,
    zoom: 14
  };

  private lastGps?: Position;

  /** Auto-zoom state */
  private smoothedSpeedKmh = 0;
  private hasSpeedSample = false;
  private targetZoom = this.state.zoom;

  /** Bearing state */
  private smoothedBearing = 0;
  private segmentBearing: number | null = null;

  /** Animation tracking */
  private isPositionAnimating = false;
  private animationStartTime = 0;
  private animationDuration = 0;
  private positionFrame?: number;

  /** Camera animation locks */
  private isCameraAnimating = false;

  /** Pending camera update while zooming */
  private pendingCameraUpdate: {
    lon?: number;
    lat?: number;
    bearing?: number;
    pitch?: number;
  } | null = null;

  /** Interpolation state (for GPS bearing calc) */
  private hasReceivedFirstFix = false;
  private startLon = 0;
  private startLat = 0;
  private targetLon = 0;
  private targetLat = 0;
  private startBearing = 0;
  private locationSubscription: Subscription | null = null;

  public followModeState: FollowModeState = FollowModeState.TWO_D_FOLLOW;
  public currentDisplayMode: DisplayMode = DisplayMode.RANGE;

  /**
   * Route planning frames the whole route with fitBounds, so GPS-driven camera
   * updates are suppressed in ROUTE mode — otherwise following would drag the
   * camera off the overview on every fix. That suppression must not swallow an
   * explicit follow-mode press though, so a user request re-enables the camera
   * until the next pan or display-mode change.
   */
  private followRequestedWhilePlanning = false;

  /**
   * Right after load the map is in RANGE mode and the first fix arrives while the
   * range is still being calculated — following would instantly recentre and zoom
   * in on the cursor, fighting the fitBounds that frames the range. GPS-driven
   * camera updates are therefore held off until the finished range has been on
   * screen for RANGE_OVERVIEW_HOLD_MS. One-shot: it only guards the first range
   * after load, so later recalculations keep behaving as before.
   */
  private rangeOverviewHoldActive = false;
  private rangeOverviewHoldConsumed = false;
  private rangeOverviewTimer?: ReturnType<typeof setTimeout>;

  private readonly cursorService = inject(CursorService);
  private readonly mapService = inject(MapService);
  private readonly locationService = inject(LocationService);
  private readonly utilsService = inject(UtilsService);
  private readonly waypointService = inject(WaypointService);
  private readonly mobileResolutionService = inject(MobileResolutionService);

  constructor() {
    this.waypointService.ObservableSelectedDisplayMode.pipe(takeUntilDestroyed()).subscribe(mode => {
      if (mode) {
        if (mode !== this.currentDisplayMode) {
          this.followRequestedWhilePlanning = false;
          this.abortRangeOverviewHold();
        }
        this.currentDisplayMode = mode;
      }
    });
  }

  public initLocationSubscription(): void {
    if (this.locationSubscription) {
      return;
    }

    this.locationSubscription = this.locationService.ObservableLastPosition.subscribe((position) => {
      if (position) {
        this.onGpsPosition(position);
      }
    });
  }

  public destroyLocationSubscription(): void {
    if (this.locationSubscription) {
      this.locationSubscription.unsubscribe();
      this.locationSubscription = null;
    }
  }

  /**
   * A range calculation has started on a freshly loaded map: freeze GPS-driven
   * camera updates so the loading animation and the range framing own the view.
   * No-op once the startup overview has already played out.
   */
  public beginRangeOverviewHold(): void {
    if (this.rangeOverviewHoldConsumed || this.rangeOverviewHoldActive) return;
    this.rangeOverviewHoldActive = true;
    this.clearRangeOverviewTimer();
  }

  /** The final range is drawn: keep it framed for a beat, then move into follow mode. */
  public scheduleRangeOverviewRelease(): void {
    if (!this.rangeOverviewHoldActive) return;
    this.clearRangeOverviewTimer();
    this.rangeOverviewTimer = setTimeout(
      () => this.finishRangeOverviewHold(true),
      this.RANGE_OVERVIEW_HOLD_MS
    );
  }

  /**
   * Drop the hold without moving the camera — the user took over, the mode
   * changed, or the calculation failed. A failed calculation passes consume =
   * false so the next attempt can still get its overview.
   */
  public abortRangeOverviewHold(consume = true): void {
    this.finishRangeOverviewHold(false, consume);
  }

  private finishRangeOverviewHold(resumeFollow: boolean, consume = true): void {
    this.clearRangeOverviewTimer();
    if (!this.rangeOverviewHoldActive) return;
    this.rangeOverviewHoldActive = false;
    if (consume) this.rangeOverviewHoldConsumed = true;

    if (!resumeFollow) return;
    if (this.followModeState === FollowModeState.PANNED) return;
    if (!this.hasReceivedFirstFix) return;

    // fitBounds left the map framed on the range, and its zoomend fed that zoom
    // back into state.zoom — fly to the speed-adaptive target instead.
    if (this.AUTO_ZOOM_ENABLED) this.state.zoom = this.targetZoom;

    this.requestCameraRender(
      this.state.lon,
      this.state.lat,
      this.state.bearing,
      this.state.pitch,
      this.state.zoom,
      this.RANGE_OVERVIEW_RESUME_MS,
      true
    );
  }

  private clearRangeOverviewTimer(): void {
    if (this.rangeOverviewTimer !== undefined) {
      clearTimeout(this.rangeOverviewTimer);
      this.rangeOverviewTimer = undefined;
    }
  }

  public setFollowModeState(followModeState: FollowModeState): void {
    // Any deliberate follow-mode change (pan, or a press on the follow button)
    // supersedes the startup range overview.
    this.abortRangeOverviewHold();

    this.followModeState = followModeState;
    if (this.followModeState == FollowModeState.PANNED) {
      // Panning hands the camera back to the user; planning may frame routes again.
      this.followRequestedWhilePlanning = false;
      return;
    }

    // A non-PANNED state is a deliberate follow request (button press, or entering
    // navigation) — honour it even during route planning.
    this.followRequestedWhilePlanning = true;

    let pitch = this.state.pitch;
    let bearing = this.state.bearing;

    switch (followModeState) {
      case FollowModeState.TWO_D_FOLLOW:
        pitch = 0;
        break;

      case FollowModeState.THREE_D_FOLLOW:
        pitch = 50;
        break;

      case FollowModeState.TWO_D_NORTH:
        pitch = 0;
        break;
    }

    this.state.pitch = pitch;
    this.state.bearing = bearing;

    // Without a fix, state.lon/lat is still (0, 0) — recentring there would throw
    // the camera into the ocean. The pitch above is kept, and the first fix applies it.
    if (!this.hasReceivedFirstFix) return;

    this.requestCameraRender(
      this.state.lon,
      this.state.lat,
      this.state.bearing,
      this.state.pitch,
      this.state.zoom,
      300,
      true
    );
  }

  private requestCameraRender(
    lon: number | undefined,
    lat: number | undefined,
    bearing: number | undefined,
    pitch: number | undefined,
    zoom: number | undefined,
    duration: number,
    forceImmediateRender = false
  ): void {
    if (!this.mapService.getMapInstance()) return;

    // Automatic (GPS-driven) updates stay suppressed while a route is being
    // planned, but a forced render is user-initiated and must always land.
    if (
      this.currentDisplayMode === DisplayMode.ROUTE &&
      !forceImmediateRender &&
      !this.followRequestedWhilePlanning
    ) return;

    // The startup range is being calculated or shown — the range framing owns
    // the camera until the hold expires.
    if (this.rangeOverviewHoldActive && !forceImmediateRender) return;

    if (this.isCameraAnimating && !forceImmediateRender) {
      this.pendingCameraUpdate = {
        lon: lon ?? this.pendingCameraUpdate?.lon,
        lat: lat ?? this.pendingCameraUpdate?.lat,
        bearing: bearing ?? this.pendingCameraUpdate?.bearing,
        pitch: pitch ?? this.pendingCameraUpdate?.pitch
      };
      return;
    }

    const effectiveBearing = this.followModeState === FollowModeState.TWO_D_NORTH ? 0 : bearing;

    this.mapService.animateMapTo(
      lon,
      lat,
      effectiveBearing,
      pitch,
      zoom,
      this.getViewOffset(),
      duration
    );

    if (forceImmediateRender) {
      this.lockCameraAndFlushAfter(duration);
    }
  }

  /**
   * Screen-space offset applied to the followed cursor (positive y = below the
   * map center, showing more road ahead). The bottom nav-plan panel's height
   * varies with orientation, open state and browser chrome, so instead of a
   * fixed screen fraction the cursor is kept CURSOR_PANEL_CLEARANCE px above
   * the panel's live bounding box whenever the panel sits under the cursor's
   * column, clamped to a sane vertical band for very short viewports.
   */
  private getViewOffset(): [number, number] {
    const h = window.innerHeight;
    const w = window.innerWidth;

    const isMobileLandscape =
      this.mobileResolutionService.getMobileResolution() &&
      !this.mobileResolutionService.isPortrait();

    /** Default "more road ahead" position when the panel doesn't constrain */
    let cursorY = h / 2 + (isMobileLandscape ? h * 0.1 : h * 0.2);

    const panel = this.getNavPlanCard()?.getBoundingClientRect();
    const panelConstrainsCursor =
      panel !== undefined &&
      panel.height > 0 &&
      panel.top < h &&                            // hidden panel slides below the viewport
      panel.left < w / 2 && panel.right > w / 2;  // wide layout parks it right of center

    if (panelConstrainsCursor) {
      cursorY = Math.min(cursorY, panel.top - this.CURSOR_PANEL_CLEARANCE);
    }

    cursorY = Math.max(
      h * this.CURSOR_MIN_Y_FRACTION,
      Math.min(h * this.CURSOR_MAX_Y_FRACTION, cursorY)
    );

    // The offset is relative to the MAP CONTAINER's center, not the window's.
    // The map is sized 100vh, which on mobile browsers with the URL bar visible
    // is taller than window.innerHeight — its center sits below innerHeight / 2,
    // so anchoring to h / 2 would push the cursor down into the panel.
    const mapRect = this.mapService.getMapInstance()?.getContainer().getBoundingClientRect();
    const mapCenterY = mapRect ? mapRect.top + mapRect.height / 2 : h / 2;

    return [0, cursorY - mapCenterY];
  }

  /** Cached lookup of the bottom nav-plan card; refetched when it re-renders. */
  private getNavPlanCard(): Element | null {
    if (!this.navPlanCard?.isConnected) {
      this.navPlanCard = document.querySelector('.np-card');
    }
    return this.navPlanCard;
  }

  private lockCameraAndFlushAfter(duration: number): void {
    this.isCameraAnimating = true;

    setTimeout(() => {
      this.isCameraAnimating = false;

      if (!this.pendingCameraUpdate) return;

      const effectiveBearing =
        this.followModeState === FollowModeState.TWO_D_NORTH
          ? 0
          : this.pendingCameraUpdate.bearing;

      this.mapService.animateMapTo(
        this.pendingCameraUpdate.lon,
        this.pendingCameraUpdate.lat,
        effectiveBearing,
        this.pendingCameraUpdate.pitch,
        this.state.zoom,
        this.getViewOffset(),
        0
      );

      this.pendingCameraUpdate = null;
    }, duration);
  }

  public onGpsPosition(position: Position): void {
    // Not just null: the webview bridge can hand over NaN, and a single NaN fix
    // would poison state.lon/lat for good — every later easeTo() would throw on
    // an invalid LngLat, so the map would stop reacting to anything, including
    // the follow-mode button.
    if (
      !Number.isFinite(position.Longitude) ||
      !Number.isFinite(position.Latitude)
    ) return;

    this.updateAutoZoomTarget(position);

    const distance = this.utilsService.haversineDistance(
      { lat: this.startLat, lon: this.startLon },
      { lat: position.Latitude, lon: position.Longitude }
    );

    // A non-finite state can never recover through the interpolation path
    // (distance would be NaN, so `distance > 1000` is false) — re-seed instead.
    const stateIsUsable =
      Number.isFinite(this.state.lon) && Number.isFinite(this.state.lat);

    if (!this.hasReceivedFirstFix || !stateIsUsable || distance > 1000) {
      this.hasReceivedFirstFix = true;

      this.state.lon = position.Longitude;
      this.state.lat = position.Latitude;

      this.startLon = position.Longitude;
      this.startLat = position.Latitude;
      this.targetLon = position.Longitude;
      this.targetLat = position.Latitude;

      this.lastGps = position;

      // Initialize bearing if possible
      if (this.shouldUseMovementBearing(position)) {
        this.state.bearing = Number.isFinite(position.Heading) ? position.Heading : this.state.bearing;
        this.smoothedBearing = this.state.bearing;
      }

      this.renderAll(0); // instant render
      return;
    }

    const duration = 1000;
    const useMovementBearing = this.shouldUseMovementBearing(position);

    this.lastGps = position;

    /** Prepare interpolation state */
    this.startLon = this.state.lon;
    this.startLat = this.state.lat;
    this.targetLon = position.Longitude;
    this.targetLat = position.Latitude;

    this.startBearing = this.state.bearing;

    const distMeters = this.utilsService.haversineDistance(
      { lat: this.startLat, lon: this.startLon },
      { lat: this.targetLat, lon: this.targetLon }
    );

    this.segmentBearing =
      (useMovementBearing && distMeters >= this.MIN_DIST_FOR_BEARING_METERS)
        ? this.utilsService.calculateBearing(
          this.startLat,
          this.startLon,
          this.targetLat,
          this.targetLon
        )
        : null;

    this.animationStartTime = performance.now();
    this.animationDuration = duration;
    this.isPositionAnimating = duration > 0;

    /** Cancel any running loop so loops can't accumulate across GPS fixes */
    if (this.positionFrame !== undefined) {
      cancelAnimationFrame(this.positionFrame);
      this.positionFrame = undefined;
    }

    /** Kick animation loop */
    if (duration > 0) {
      this.positionFrame = requestAnimationFrame(
        (t) => this.animatePositionFrame(t, useMovementBearing)
      );
    } else {
      this.state.lon = this.targetLon;
      this.state.lat = this.targetLat;

      if (useMovementBearing && this.segmentBearing !== null) {
        this.state.bearing = this.segmentBearing;
        this.smoothedBearing = this.segmentBearing;
      }

      this.renderAll(0);
    }
  }

  private animatePositionFrame(
    now: number,
    calculateBearing: boolean
  ): void {
    const t = Math.min(
      (now - this.animationStartTime) / this.animationDuration,
      1
    );

    const deltaLon =
      this.utilsService.shortestAngleDelta(
        this.startLon,
        this.targetLon
      );

    const lon = this.startLon + deltaLon * t;
    const lat = this.startLat + (this.targetLat - this.startLat) * t;

    if (calculateBearing && this.segmentBearing !== null) {
      this.state.bearing = this.utilsService.interpolateBearing(
        this.startBearing,
        this.segmentBearing,
        t
      );
      this.smoothedBearing = this.state.bearing; // keep internal bearing state consistent
    }

    this.state.lon = lon;
    this.state.lat = lat;

    this.renderAll(0);

    if (t < 1) {
      this.positionFrame = requestAnimationFrame(
        (n) => this.animatePositionFrame(n, calculateBearing)
      );
    } else {
      this.positionFrame = undefined;
      this.isPositionAnimating = false;
    }
  }

  private renderAll(duration: number): void {
    if (this.mapService.getMapInstance() != null) {
      if (this.cursorService.isCursorInitialized()) {
        this.cursorService.animateCursorTo(this.state.lon, this.state.lat, this.state.bearing, duration);
      }
      else {
        this.cursorService.initCursorAt(this.state.lon, this.state.lat, this.mapService.getMapInstance()!)
          .catch(() => { /* style still loading — retried on the next position update */ });
      }

      if (this.followModeState !== FollowModeState.PANNED) {
        if (this.AUTO_ZOOM_ENABLED) {
          this.state.zoom += (this.targetZoom - this.state.zoom) * this.AUTO_ZOOM_EASE;
        }
        this.requestCameraRender(
          this.state.lon,
          this.state.lat,
          this.state.bearing,
          this.state.pitch,
          this.state.zoom,
          duration
        );
      }
    }
  }

  private shouldUseMovementBearing(position: Position): boolean {
    const speedOk =
      position.Speed != null &&
      position.Speed >= this.MIN_SPEED_FOR_GPS_BEARING;

    const replayMode =
      this.locationService.getFakeGPSReplayMode();

    return speedOk || replayMode;
  }

  /**
   * Normalize a fix's speed to km/h. Live GPS stores coords.speed in m/s (unconverted),
   * while replay injects km/h directly — the replay flag tells the two apart. Returns
   * null when speed is unknown so callers can hold the current target.
   */
  private getSpeedKmh(position: Position): number | null {
    if (position.Speed == null || position.Speed < 0) return null;
    return this.locationService.getFakeGPSReplayMode()
      ? position.Speed                               // replay: already km/h
      : this.utilsService.mpsToKmph(position.Speed); // live GPS: m/s -> km/h
  }

  /** Linear-interpolate the target zoom for a speed, clamped to the curve's ends. */
  private zoomForSpeed(speedKmh: number): number {
    const curve = this.ZOOM_SPEED_CURVE;
    if (speedKmh <= curve[0].speedKmh) return curve[0].zoom;

    const last = curve[curve.length - 1];
    if (speedKmh >= last.speedKmh) return last.zoom;

    for (let i = 0; i < curve.length - 1; i++) {
      const lo = curve[i];
      const hi = curve[i + 1];
      if (speedKmh >= lo.speedKmh && speedKmh <= hi.speedKmh) {
        const t = (speedKmh - lo.speedKmh) / (hi.speedKmh - lo.speedKmh);
        return lo.zoom + (hi.zoom - lo.zoom) * t;
      }
    }
    return last.zoom;
  }

  /** Fold a new fix's speed into the smoothed value and recompute the zoom target. */
  private updateAutoZoomTarget(position: Position): void {
    if (!this.AUTO_ZOOM_ENABLED) return;

    const kmh = this.getSpeedKmh(position);
    if (kmh == null) return; // unknown speed — keep the current target

    if (!this.hasSpeedSample) {
      this.hasSpeedSample = true;
      this.smoothedSpeedKmh = kmh; // seed to avoid a startup ramp from 0
    } else {
      this.smoothedSpeedKmh += (kmh - this.smoothedSpeedKmh) * this.SPEED_SMOOTHING;
    }
    this.targetZoom = this.zoomForSpeed(this.smoothedSpeedKmh);
  }

  public zoomIn(): void {
    this.setZoom(
      this.state.zoom + this.ZOOM_STEP,
      this.ZOOM_ANIMATION_DURATION
    );
  }

  public zoomOut(): void {
    this.setZoom(
      this.state.zoom - this.ZOOM_STEP,
      this.ZOOM_ANIMATION_DURATION
    );
  }

  public setZoom(
    zoom: number,
    duration = this.ZOOM_ANIMATION_DURATION
  ): void {
    if (!this.mapService.getMapInstance()) return;

    const clamped = Math.max(
      this.MIN_ZOOM,
      Math.min(this.MAX_ZOOM, zoom)
    );

    this.state.zoom = clamped;
    this.requestCameraRender(
      this.followModeState !== FollowModeState.PANNED ? this.state.lon : undefined,
      this.followModeState !== FollowModeState.PANNED ? this.state.lat : undefined,
      undefined,
      undefined,
      this.state.zoom,
      duration,
      true
    );
  }

  public onUserZoomEnd(zoom: number): void {
    this.state.zoom = zoom;
  }
}
